import ITeamMember from "../../../models/ITeamMember";
import { Image, makeStyles, Text, Title2 } from "@fluentui/react-components";
import teamMemberService from "../../../services/TeamMemberService";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface ITeamMemberProps {
    member: ITeamMember;
}

const useStyles = makeStyles({
    sectionTitle: {
        fontSize: "2rem",
        marginBottom: "2rem",
        margin: "0 auto",

        "& > h2": {
            textAlign: "center",
        }
    },
    teamMembersContainer: {
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        paddingBottom: "2rem",
        
        "@media (min-width: 768px)": {
            maxWidth: "1200px",
            margin: "0 auto"
        }
    },
    teamMember: {
        flex: "1 1 100%",
        maxWidth: "100%",
        margin: "2rem 1rem",
        boxSizing: "border-box",
        
        "@media (min-width: 576px)": {
            flex: "1 1 45%",
            maxWidth: "45%"
        },
        
        "@media (min-width: 768px)": {
            flex: "1 1 22%",
            maxWidth: "22%"
        }
    },
    memberCard: {
        padding: "1rem",
        borderRadius: "8px",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        height: "100%",

        "@media (max-width: 575px)": {
            flexDirection: "row",
            textAlign: "left"
        },

        "& img": {
            width: "100%",
            height: "auto",
            borderRadius: "50%",
            marginBottom: "1rem",
            
            "@media (max-width: 575px)": {
                width: "40%",
                marginBottom: "0",
                marginRight: "1rem"
            }
        },

        "& div": {
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            width: "100%",

            "& >span": {
                textAlign: "center",
            }
        },
    },
    name: {
        fontSize: "1.2rem",
        margin: "0.5rem auto 0 auto",
        textAlign: "center"
    },
    jobTitle: {
        fontSize: "1rem",
        color: "#666",
        fontWeight: 500,
        margin: "0.5rem auto",
        
        "@media (max-width: 768px)": {
            marginTop: "0.5rem"
        }
    }
});

const TeamMember = (props: ITeamMemberProps) => {
    const { member } = props;
    const styles = useStyles();

    const photoUrl = teamMemberService.getTeamMemberPhotoUrl(member.id);

    return (
        <div className={styles.teamMember}>
            <div className={styles.memberCard}>
                <Image src={photoUrl} alt={member.name} />
                <div>
                    <Text block className={styles.name}>
                        {member.name}
                    </Text>
                    <Text block className={styles.jobTitle}>
                        {member.position}
                    </Text>
                </div>
            </div>
        </div>
    );
};

const TeamMembers = () => {
    const { t } = useTranslation("ourstory");

    const styles = useStyles();

    const [teamMembers, setTeamMembers] = useState<ITeamMember[]>([]);

    useEffect(() => {
        getTeamMembers(setTeamMembers);
    }, []);

    return (
        <section>
            <div className={styles.sectionTitle}>
                <Title2 block as="h2">{t("team.title")}</Title2>
            </div>
            <div className={styles.teamMembersContainer}>
                {teamMembers.map((member) => <TeamMember key={member.id} member={member} />)}
            </div>
        </section>
    );
};

export default TeamMembers;

export async function getTeamMembers(setTeamMembers: (teamMembers: ITeamMember[]) => void) {
    try {
        let members = await teamMemberService.getTeamMembers();
        members = members.sort((a, b) => a.name.localeCompare(b.name));

        setTeamMembers(members);
    } catch (error) {
        console.error("Failed to fetch team members: ", error);
    }
}